import { FC, useCallback, useContext, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

import useResendActivationEmail from "../../../../apis/auth/useResendActivationEmail/useResendActivationEmail";
import { NotificationContext } from "../../../../context/NotificationContext";

interface ForgotPasswordModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
}

const ResendActivationEmail: FC<ForgotPasswordModalProps> = ({
  isModalOpen,
  closeModal,
}) => {
  const resendActivationEmail = useResendActivationEmail();
  const { createNotification } = useContext(NotificationContext);
  const { control, handleSubmit } = useForm({
    defaultValues: { emailAddress: "" },
  });

  const onSubmit = useCallback(
    (data: { emailAddress: string }) => {
      resendActivationEmail.mutate(data.emailAddress);
    },
    [resendActivationEmail]
  );

  useEffect(() => {
    if (resendActivationEmail.isSuccess) {
      createNotification({
        message: "Activation link has been sent.",
        type: "success",
        title: "Resend Activation Link Notification",
      });
    }
  }, [resendActivationEmail.isSuccess, createNotification]);

  useEffect(() => {
    if (resendActivationEmail.isError) {
      createNotification({
        message:
          resendActivationEmail.error?.response?.data?.userMessage ||
          "An Error occurred while sending activation link",
        type: "error",
        title: "Resend Activation Link Notification",
      });
    }
    // eslint-disable-next-line
  }, [resendActivationEmail.isError]);

  return (
    <Dialog
      open={isModalOpen}
      onClose={closeModal}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      <DialogTitle
        className="firm-account-title red-pastel"
        sx={{ m: 0, p: 2 }}
        id="scroll-dialog-title"
      >
        Resend Activation Link
        <IconButton
          onClick={closeModal}
          aria-label="Close Resend Activation Link dialog"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[900],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
        <DialogContent>
          <div className="filed-slide mb-4">
            <Controller
              name="emailAddress"
              control={control}
              rules={{
                required: "Error: Email is required.",
                pattern: {
                  //value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, Commented for PR 5641742
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address.",
                },
              }}
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  className="w-100 m-0"
                  id={field.name}
                  autoFocus
                  inputRef={ref}
                  {...field}
                  onBlur={() => {
                    field.onChange(field.value?.trim());
                    field.onBlur();
                  }}
                  placeholder="Your eFileIL email address"
                  label="eFileIL email address"
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error ? fieldState.error.message : null
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MarkunreadIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <DialogContentText>
            An Activation Link will be sent to the email address associated with
            your eFileIL Account.
          </DialogContentText>

          <div className="resend-activation-steps-card">
            <p className="resend-activation-steps-card-title">What to do next?</p>
            <div className="resend-activation-steps">
              <div className="resend-activation-step">
                <div className="resend-activation-step-icon-wrap step-email">
                  <EmailOutlinedIcon />
                  <span className="resend-activation-step-number">1</span>
                </div>
                <p className="resend-activation-step-title">Check Email</p>
                <p className="resend-activation-step-desc">
                  Look for the activation link in your inbox sent from Tyler - A state wide EFSP. 
                </p>
              </div>

              <ArrowRightAltIcon className="resend-activation-step-arrow" />

              <div className="resend-activation-step">
                <div className="resend-activation-step-icon-wrap step-process">
                  <LinkIcon />
                  <span className="resend-activation-step-number">2</span>
                </div>
                <p className="resend-activation-step-title">Complete Process</p>
                <p className="resend-activation-step-desc">
                  Follow the link to activate your account.
                </p>
                <p className="resend-activation-step-note">
                  Do not sign in to Tyler after completing the activation.
                </p>
              </div>

              <ArrowRightAltIcon className="resend-activation-step-arrow" />

              <div className="resend-activation-step">
                <div className="resend-activation-step-icon-wrap step-login">
                  <LockOutlinedIcon />
                  <span className="resend-activation-step-number">3</span>
                </div>
                <p className="resend-activation-step-title">Log In</p>
                <p className="resend-activation-step-desc">
                  Once completed, kindly login back to{" "}
                  <strong>
                    <a
                      href={
                        process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
                          ? "https://efileil.i2file.net"
                          : "https://test.i2file.net"
                      }
                      style={{ color: "#676767" }}
                    >
                      {process.env.REACT_APP_ENVIRONMENT === "PRODUCTION"
                        ? "efileil.i2file.net"
                        : "test.i2file.net"}
                    </a>
                  </strong>{" "}
                  to continue your request.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <IconButton
            className="btn primary-button"
            type="submit"
            disabled={resendActivationEmail.isLoading}
          >
            {resendActivationEmail.isLoading ? (
              <CircularProgress
                sx={{
                  height: "25px !important",
                  width: "25px !important",
                  "--CircularProgress-size": "8px",
                  "--CircularProgress-trackThickness": "1px",
                  "--CircularProgress-progressThickness": "1px",
                }}
                color="inherit"
              />
            ) : (
              "Ok"
            )}
          </IconButton>
          <Button
            type="button"
            variant="contained"
            className="btn button-outline-secondary"
            onClick={closeModal}
          >
            Cancel
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ResendActivationEmail;
